import { useState, useEffect, useRef, type ReactNode } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  ArrowLeft, AlertTriangle, Sparkles,
  LayoutDashboard, Wrench, Boxes, Star, CreditCard, MessageSquare, History, PenLine, FolderOpen,
  type LucideIcon,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Tabs } from "@/components/ui/Tabs";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { useCustomer, useCustomerDispatches, useEnsureSignalsExtracted, type LocationRow } from "./api";
import { CustomerHeader } from "./CustomerHeader";
import { EditCustomerProvider } from "./editCustomerContext";
import { PropertyRow } from "./CustomerRail";
import { AnswerStrip } from "./AnswerStrip";
import { DispatchPeek } from "./DispatchPeek";
import { CustomerDispatches } from "./CustomerDispatches";
import { CustomerOverview, CustomerMoneyTab } from "./CustomerOverview";
import { CustomerInvoices } from "./CustomerInvoices";
import { CustomerHistory } from "./CustomerHistory";
import { CustomerNotes } from "./CustomerNotes";
import { CustomerEquipment } from "./CustomerEquipment";
import { CustomerAgreements } from "./CustomerAgreements";
import { CustomerNavigation } from "./CustomerNavigation";
import { CustomerMessages } from "./CustomerMessages";
import { PortalCommsCard } from "./PortalCommsCard";
import { ReachCard } from "./ReachCard";
import { SentComms } from "@/features/jobs/SentComms";
import { CustomerProposals } from "./CustomerProposals";
import { CustomerDocuments } from "./CustomerDocuments";
import { displayCustomerName } from "@/lib/format";
import { useBackTarget, backText } from "@/lib/navBack";
import { useRecentCustomers } from "./useRecentCustomers";

type TabKey =
  | "overview"
  | "service"
  | "equipment"
  | "membership"
  | "money"
  | "comms"
  | "history"
  | "notes"
  | "files";

// C360-OVERVIEW-02 (Seth 2026-09-30, on real data): the board's one-big-
// timeline overview drowned the glance cards ("the stuff on the right is
// very important and it's being squished… the timeline should be found
// under Invoices and under History"). So the full merged feed keeps its
// OWN History pill, Notes keeps its own home, and the Overview goes back
// to the balanced operational layout — under the board's new hero, tiles
// and Ask strip, which stay.
// TAB-MEMBERSHIP-02 (Seth 2026-10-06, after testing a Pegasus plan sale: "I
// don't like how the membership tab is gone and that it takes you to
// 'money'"): memberships get their own pill back. They're an operational
// world — checks owed, booking, renewals, accessories — not just billing.
// Money keeps the financial story (summary, invoices, payments) plus a small
// membership pointer so nobody gets lost.
const tabs: { key: TabKey; label: string; placeholder?: boolean }[] = [
  { key: "overview", label: "Overview" },
  { key: "service", label: "Service" },
  { key: "equipment", label: "Equipment" },
  { key: "membership", label: "Membership" },
  { key: "money", label: "Money" },
  { key: "comms", label: "Comms" },
  { key: "history", label: "History" },
  { key: "notes", label: "Notes" },
  { key: "files", label: "Files" },
];

/** Every pre-consolidation tab key → its pill (deep links never break). */
const TAB_ALIASES: Record<string, TabKey> = {
  dispatches: "service",
  quotes: "service",
  agreements: "membership",
  invoices: "money",
  messages: "comms",
  timeline: "history",
  documents: "files",
};

// TAB-SPEED-01 (Seth 2026-09-17: "clicking through the 360 tabs is really slow
// to load and change"). Tabs used to REMOUNT on every switch: each click re-ran
// the tab's queries and rebuilt its whole list from scratch, with a skeleton
// (or History's "no activity" flash) in between. Now a tab mounts ONCE per
// customer and stays mounted, hidden, so coming back is a style toggle — no
// refetch, no skeleton, no rebuild. The other tabs warm up in the background a
// couple of seconds after the page opens, one at a time, so the FIRST click on
// each is instant too. Freshness is kept by a stale sweep on tab switch (below).
const WARM_ORDER: TabKey[] = [
  "service", "history", "membership", "money", "equipment", "comms", "notes", "files",
];
/** Let the Overview land and paint before the warm-up starts. */
const WARM_START_MS = 2500;
/** One tab at a time — gentle on the connection and the DB. */
const WARM_STEP_MS = 400;
/** Kept-alive tabs no longer refetch on re-show, so on a tab switch this
 *  customer's mounted queries that are stale (by their own staleTime) refetch
 *  in the background — at most once per two minutes, never a skeleton. */
const STALE_SWEEP_MS = 2 * 60 * 1000;

// NAV-360-01: every tab wears an icon (icon + label, never icon riddles).
const TAB_ICON: Record<TabKey, LucideIcon> = {
  overview: LayoutDashboard, service: Wrench, equipment: Boxes, membership: Star,
  money: CreditCard, comms: MessageSquare, history: History, notes: PenLine, files: FolderOpen,
};

/** NAV-360-01: counts on the tabs that answer "is anything there?" before the
 *  click — service & equipment follow the selected property; money and files
 *  follow the account (money follows the CUSTOMER, per the scope rules). */
function useTabCounts(custno: string | undefined, locno: string | null | undefined) {
  return useQuery({
    queryKey: ["tab_counts", custno, locno ?? "all"],
    enabled: !!custno,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<Partial<Record<TabKey, number>>> => {
      const head = { count: "exact" as const, head: true };
      let disp = supabase.from("mirror_dispatch").select("dispatch", head).eq("custno", custno!);
      let equip = supabase.from("mirror_equipment").select("counter", head).eq("custno", custno!);
      if (locno) { disp = disp.eq("locno", locno); equip = equip.eq("locno", locno); }
      const [d, e, iEsc, iPeg, f] = await Promise.all([
        disp, equip,
        supabase.from("mirror_sales").select("invoice", head).eq("custno", custno!),
        supabase.from("pegasus_job_invoice").select("id", head).eq("custno", custno!),
        supabase.from("pegasus_file").select("id", head).eq("custno", custno!),
      ]);
      const n = (r: { count: number | null }) => r.count ?? 0;
      return {
        service: n(d), equipment: n(e),
        money: n(iEsc) + n(iPeg),
        files: n(f),
      };
    },
  });
}

export function CustomerDetailView() {
  const { custno } = useParams<{ custno: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: customer, isLoading, isError, error } = useCustomer(custno);
  // "Back to search" preserves the q param so the dispatcher returns to
  // their populated result list, not a blank search page.
  const fromQ = searchParams.get("q");
  const backToSearchUrl = fromQ
    ? `/customers?q=${encodeURIComponent(fromQ)}`
    : "/customers";

  // Back: prefer the recorded referrer (NAV-03); fall back to the legacy
  // ?from= contexts (agreements worklist / search) or the search page.
  const fromContext = searchParams.get("from");
  const fallbackBack =
    fromContext === "agreements"
      ? { label: "agreements", url: "/agreements" }
      : { label: "search", url: backToSearchUrl };
  const back = useBackTarget(fallbackBack);

  const [selectedLocation, setSelectedLocation] = useState<LocationRow | null>(
    null,
  );
  // C360-BOARD-01: the header pill's in-place property switcher.
  const [propertyPickerOpen, setPropertyPickerOpen] = useState(false);
  // HDR-DISPATCH-01 (Seth 2026-10-20, "B is the header, always"): the band is
  // ONE compact row at every scroll position, so HDR-CONDENSE-01's scroll
  // listener (two heights + hysteresis) is gone — nothing to condense into.
  // NAV-360-01: live counts for the tab bar.
  const tabCounts = useTabCounts(customer?.custno, selectedLocation?.locno ?? null).data ?? {};
  // 360-B: the Next / Last visit tiles sit above the tabs and peek a dispatch
  // in place — same cache the Overview's list reads (React Query dedupes).
  const { data: dispatchRows } = useCustomerDispatches(custno);
  const [peekDispatch, setPeekDispatch] = useState<string | null>(null);
  // The inline Ask box's draft (Seth 2026-10-13: "can I not type right in
  // that box?") — Enter hands it to the Ask Pegasus popup.
  const [askDraft, setAskDraft] = useState("");
  // RECENTS-01 (Seth 2026-10-13: "since I viewed their 360 … it is going to
  // show up in the recent list"): VIEWING a 360 is what makes a customer
  // recent — not just clicking a search result. Any road here counts.
  const { push: pushRecent } = useRecentCustomers();
  useEffect(() => {
    if (custno) pushRecent(custno);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [custno]);
  // Tab lives in the URL (?tab=) so it's deep-linkable AND captured when
  // another screen records us as its "back" target. Reading from the URL (not
  // local state) keeps the current tab in the address bar at all times.
  const tabParam = searchParams.get("tab");
  const tab: TabKey = tabs.some((t) => t.key === tabParam)
    ? (tabParam as TabKey)
    : TAB_ALIASES[tabParam ?? ""] ?? "overview";
  const setTab = (k: TabKey) =>
    setSearchParams(
      (prev) => {
        prev.set("tab", k);
        return prev;
      },
      { replace: true },
    );

  // TAB-CONSOLIDATE-01: an old deep link (?tab=invoices, ?tab=agreements,
  // ?tab=quotes) lands on the consolidated pill AND scrolls to the block it
  // used to point at, so nothing anyone bookmarked or wired ever misses.
  useEffect(() => {
    const raw = searchParams.get("tab");
    if (!raw || !TAB_ALIASES[raw] || raw === tab) return;
    const t = window.setTimeout(() => {
      document.getElementById(`sub-${raw}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 350);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [custno, searchParams]);

  // TAB-SPEED-01: the tabs opened for THIS customer, in the order they were
  // opened — every one of them stays mounted. Derived during render (the
  // current tab is always in) so a fresh tab never paints an empty frame
  // before an effect catches up; a new customer starts the list over.
  const [visited, setVisited] = useState<{ custno?: string; tabs: TabKey[] }>({ custno, tabs: [] });
  const visitedTabs = visited.custno === custno ? visited.tabs : [];
  const openTabs: TabKey[] = visitedTabs.includes(tab) ? visitedTabs : [...visitedTabs, tab];
  useEffect(() => {
    setVisited((v) => {
      const cur = v.custno === custno ? v.tabs : [];
      if (cur.includes(tab) && v.custno === custno) return v;
      return { custno, tabs: cur.includes(tab) ? cur : [...cur, tab] };
    });
  }, [custno, tab]);

  // Warm the other tabs in the background once the page has landed: mount
  // them hidden, one every WARM_STEP_MS, so their data is already here when
  // the first click comes. Paused while the browser tab is in the background
  // (a page nobody is looking at should not pull data), stopped on leave.
  const loadedCustno = customer?.custno;
  useEffect(() => {
    if (!loadedCustno) return;
    let cancelled = false;
    let i = 0;
    let timer = 0;
    const step = () => {
      if (cancelled) return;
      if (document.visibilityState === "hidden") {
        timer = window.setTimeout(step, 2000);
        return;
      }
      const next = WARM_ORDER[i++];
      if (!next) return;
      setVisited((v) => {
        const cur = v.custno === loadedCustno ? v.tabs : [];
        return cur.includes(next) ? v : { custno: loadedCustno, tabs: [...cur, next] };
      });
      timer = window.setTimeout(step, WARM_STEP_MS);
    };
    timer = window.setTimeout(step, WARM_START_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [loadedCustno]);

  // Stale sweep: kept-alive tabs never remount, so on a tab switch refetch
  // this customer's mounted queries that are stale by their own staleTime —
  // in the background, cached data stays on screen. Throttled to once per
  // STALE_SWEEP_MS; a fresh page start counts as swept (everything is new).
  const lastSweep = useRef(0);
  const sweepCustno = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!custno) return;
    const now = Date.now();
    if (sweepCustno.current !== custno) {
      sweepCustno.current = custno;
      lastSweep.current = now;
      return;
    }
    if (now - lastSweep.current < STALE_SWEEP_MS) return;
    lastSweep.current = now;
    void queryClient.invalidateQueries(
      {
        refetchType: "active",
        predicate: (q) => q.queryKey.includes(custno) && q.state.dataUpdatedAt > 0 && q.isStale(),
      },
      // A request already running is left alone — never restart in-flight work.
      { cancelRefetch: false },
    );
  }, [custno, tab, queryClient]);

  // 360-AUDIT #1 (2026-08-21): prev/next cycling and cross-links kept the
  // PREVIOUS customer's LocationRow — wrong address/contacts under the new
  // name, every locno-filtered card querying the new custno with the OLD
  // locno. Clear the selection the moment the account changes.
  useEffect(() => {
    setSelectedLocation(null);
    setPropertyPickerOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [custno]);

  // NAV-360-02: the property switcher is a true dropdown now — Esc closes
  // it (the invisible overlay handles click-outside).
  useEffect(() => {
    if (!propertyPickerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPropertyPickerOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [propertyPickerOpen]);

  // When the customer loads, default-select the first active location — or the
  // specific location from a ?loc= deep link (e.g. the property-history card).
  useEffect(() => {
    if (!customer) return;
    if (selectedLocation && (customer.locations ?? []).some((l) => l.locno === selectedLocation.locno)) return;
    const locs = customer.locations ?? [];
    const locParam = searchParams.get("loc");
    const target =
      (locParam ? locs.find((l) => l.locno === locParam) : null) ??
      locs.find((l) => !l.location_inactive) ??
      locs[0] ??
      null;
    setSelectedLocation(target);
  }, [customer, selectedLocation, searchParams]);

  // Browser tab shows the customer's name when on their page
  useEffect(() => {
    if (customer?.fullname) {
      const name = displayCustomerName(
        selectedLocation?.locname,
        customer.fullname,
      );
      document.title = `Pegasus · ${name}`;
    }
  }, [customer, selectedLocation]);

  // NOTES-AI v8 — self-running, PER-LOCATION extraction: reads THIS property's
  // notes in the background so the Good-to-Know pills are scoped to the unit,
  // not shared across a property manager's sites. No-op once marked.
  useEnsureSignalsExtracted(custno, selectedLocation?.locno);

  if (isLoading) {
    return (
      <div className="mx-auto max-w-7xl space-y-6 p-8">
        <Card className="h-32 animate-pulse bg-slate-100" />
        <Card className="h-48 animate-pulse bg-slate-100" />
      </div>
    );
  }

  if (isError || !customer) {
    return (
      <div className="mx-auto max-w-2xl p-8">
        <Card className="border-red-200 bg-red-50 p-5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 text-red-600" />
            <div>
              <h3 className="text-sm font-semibold text-red-900">
                Couldn&apos;t load customer {custno}
              </h3>
              <p className="mt-1 text-xs text-red-700">
                {(error as Error)?.message ?? "Customer not found."}
              </p>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate(back.url)}
                className="mt-3"
              >
                {backText(back.label)}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  const visitRows = (dispatchRows ?? []).filter((r) => (selectedLocation?.locno ? r.locno === selectedLocation.locno : true));
  // NAV-360-02: the dropdown lists active properties (same rule the old
  // inline panel used).
  const menuLocations = (customer.locations ?? []).filter((l) => !l.location_inactive);

  return (
    <EditCustomerProvider>
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.15 }}
      // PAGE-FRAME-01 (DESIGN-DENSITY-01, Seth approved Direction B
      // 2026-10-12): record pages live in the 1240px reading column —
      // the 1560px stretch was why the band felt sparse-yet-busy on the
      // office monitors. .page-record is THE knob (src/index.css).
      className="c360 page-record p-6 lg:p-8"
    >
      {/* 360-AUDIT wave 2 (E): shared error surface for the one-click writes. */}

      {/* Back link above the band (Seth 2026-09-08: "put the back to search
          above the map box"). The prev/next pager moved INTO the band's
          right cluster (NAV-360-02 / Exhibit A) — keyboard ←/→ unchanged. */}
      <div className="mb-2 flex items-center gap-4">
        <button
          type="button"
          onClick={() => navigate(back.url)}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 transition-colors duration-150 ease-out-quart hover:text-brand-700"
        >
          <ArrowLeft className="h-3 w-3" />
          {backText(back.label)}
        </button>
      </div>

      {/* C360-BOARD-01 (the approved C360After board, 2026-09-30): ONE
          column — the hero header (identity · address + property pill ·
          one-tap contact · Book a Call), the four pulse tiles, the Ask
          strip, six pills, then the tab body. The old left profile rail
          retired — its blocks live on inside the Overview's context rail
          and the header. */}
      <div className="min-w-0">
        {/* NAV-360-02 (the approved Exhibit A, built for real after 34i34
            only re-skinned the tab strip): identity · property trigger ·
            pager · tab bar are ONE band, and the whole unit rides sticky —
            the page scrolls under its glass. */}
        <div className="sticky top-0 z-30 pb-2 pt-1.5">
          <CustomerHeader
            customer={customer}
            selectedLocation={selectedLocation}
            onSelectLocation={setSelectedLocation}
            onGoTab={(k) => setTab(k as TabKey)}
            onProperties={() => setPropertyPickerOpen((v) => !v)}
            propertyPickerOpen={propertyPickerOpen}
            pager={custno ? <CustomerNavigation currentCustno={custno} /> : null}
            propertyMenu={
              propertyPickerOpen ? (
                <>
                  {/* click-away catcher under the menu */}
                  <div className="fixed inset-0 z-30" onClick={() => setPropertyPickerOpen(false)} />
                  {/* Exhibit C: a TRUE dropdown — anchored to the trigger,
                      floating over the page, 160ms scale-in, Esc to close.
                      Same PropertyRow rows (search · $ lifetime · check ·
                      Add / Move) the inline bar carried. */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.97, y: -4 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ duration: 0.16, ease: [0.16, 1, 0.3, 1] }}
                    // NAV-360-03: the trigger moved into the scope bar (left
                    // end of the tab row), so the menu drops left-aligned.
                    className="absolute left-0 top-[calc(100%+2px)] z-40 w-[min(440px,calc(100vw-3rem))] origin-top-left overflow-hidden rounded-2xl border border-hairline bg-white shadow-e3"
                  >
                    <div className="px-3.5 pb-1.5 pt-2.5 text-[11px] font-extrabold uppercase tracking-[0.08em] text-slate-400">
                      {displayCustomerName(null, customer.fullname)} · {menuLocations.length} propert{menuLocations.length === 1 ? "y" : "ies"}
                    </div>
                    <PropertyRow
                      bare
                      custno={customer.custno}
                      locations={menuLocations}
                      selected={selectedLocation}
                      onSelect={(l) => { setSelectedLocation(l); setPropertyPickerOpen(false); }}
                    />
                  </motion.div>
                </>
              ) : null
            }
            tabs={
              <Tabs
                variant="bar"
                size="sm"
                className="border-b-0"
                value={tab}
                onChange={(k) => setTab(k as TabKey)}
                items={tabs.map((t) => ({
                  key: t.key,
                  label: t.label,
                  icon: TAB_ICON[t.key],
                  count: tabCounts[t.key] || null,
                  soon: t.placeholder,
                }))}
              />
            }
          />
        </div>

        {/* NAV-360-04: the prose/tenure story line lives INSIDE the band now. */}

        {/* The four answers, big, above the tabs — the board's tiles:
            NEXT VISIT · OPEN ITEMS · BALANCE · PLAN. */}
        <AnswerStrip
          custno={customer.custno}
          locno={selectedLocation?.locno}
          rows={visitRows}
          multiProperty={customer.locations.length > 1}
          onPeek={setPeekDispatch}
          cells={["next", "open", "balance", "plan"]}
          className="mt-4"
        />
        <DispatchPeek dispatchId={peekDispatch} onClose={() => setPeekDispatch(null)} />

        {/* Ask about THIS customer — a REAL input (Seth 2026-10-13: "can I
            not type right in that box?"). Type here; Enter (or Ask) hands the
            question to Ask Pegasus, which reads the route and answers
            account-scoped. Clicking just focuses — no surprise popup. */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const q = askDraft.trim();
            if (!q) return;
            window.dispatchEvent(new CustomEvent("pegasus:ask-open", { detail: { q } }));
            setAskDraft("");
          }}
          className="mt-2.5 flex w-full items-center gap-2.5 rounded-full border border-hairline bg-white/80 px-4 py-1.5 shadow-e1 transition-all focus-within:border-brand-300 focus-within:shadow-e2 hover:border-brand-300"
        >
          <Sparkles className="h-4 w-4 shrink-0 text-brand-500" />
          <input
            value={askDraft}
            onChange={(e) => setAskDraft(e.target.value)}
            placeholder={`Ask about ${displayCustomerName(null, customer.fullname).split(",")[0].split(" ")[0]}… "when was the furnace installed?" · "do they owe anything?"`}
            className="h-7 w-full min-w-0 bg-transparent text-[13px] text-slate-700 placeholder:text-slate-400 focus:outline-none"
          />
          {askDraft.trim() !== "" && (
            <button
              type="submit"
              className="shrink-0 rounded-full bg-brand-600 px-3 py-1 text-[12px] font-bold text-white transition-colors hover:bg-brand-700"
            >
              Ask
            </button>
          )}
        </form>

        {/* Tab body (TAB-SPEED-01) — every tab opened for this customer is
            rendered and kept; only the current one is shown. A tab's own
            crash stays inside its panel; the rest of the page keeps working.
            (The tab BAR itself lives inside the band above — NAV-360-02.) */}
        <div className="mt-5">
          {openTabs.map((k) => (
            <div
              key={k}
              role="tabpanel"
              hidden={k !== tab}
              className={k === tab ? "c360-panel-in" : undefined}
            >
              <ErrorBoundary label={`the ${tabs.find((t) => t.key === k)?.label ?? k} tab`}>
                {renderTab(k)}
              </ErrorBoundary>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
    </EditCustomerProvider>
  );

  function renderTab(k: TabKey): ReactNode {
    if (!customer) return null;
    switch (k) {
      case "overview":
        return (
          <CustomerOverview
            custno={customer.custno}
            locno={selectedLocation?.locno}
            multiProperty={customer.locations.length > 1}
            onViewAllService={() => setTab("service")}
            customer={customer}
            selectedLocation={selectedLocation}
            onOpenEquipment={() => setTab("equipment")}
          />
        );
      case "service":
        // Visits first, then the selling that rides beside them. The Quotes
        // block keeps its old ?tab=quotes deep-link target as an anchor.
        return (
          <div className="space-y-6">
            <div id="sub-dispatches" className="scroll-mt-24">
              <CustomerDispatches
                custno={customer.custno}
                locno={selectedLocation?.locno}
              />
            </div>
            <div id="sub-quotes" className="scroll-mt-24">
              <CustomerProposals custno={customer.custno} locno={selectedLocation?.locno ?? null} />
            </div>
          </div>
        );
      case "equipment":
        return (
          <CustomerEquipment
            custno={customer.custno}
            locno={selectedLocation?.locno}
            onOpenAgreement={() => setTab("membership")}
          />
        );
      case "membership":
        // TAB-MEMBERSHIP-02: plans, checks, renewals, accessories — the whole
        // membership world on its own pill again.
        return (
          <div id="sub-agreements" className="scroll-mt-24">
            <CustomerAgreements
              custno={customer.custno}
              locno={selectedLocation?.locno}
            />
          </div>
        );
      case "money":
        // The financial story: summary, then invoices. Membership moved back
        // to its own pill (TAB-MEMBERSHIP-02); a pointer keeps old habits
        // working.
        return (
          <div className="space-y-6">
            <CustomerMoneyTab custno={customer.custno} multiProperty={customer.locations.length > 1} />
            <div id="sub-invoices" className="scroll-mt-24">
              <CustomerInvoices
                custno={customer.custno}
                locno={selectedLocation?.locno}
                customerName={displayCustomerName(selectedLocation?.locname, customer.fullname)}
                multiProperty={customer.locations.length > 1}
                locations={customer.locations}
              />
            </div>
            <button
              type="button"
              onClick={() => setTab("membership")}
              className="flex w-full items-center justify-between gap-2 rounded-xl border border-dashed border-slate-200 px-4 py-3 text-left text-sm text-slate-600 transition-colors hover:border-brand-300 hover:bg-brand-50/40"
            >
              <span>Looking for their plan? Memberships have their own tab now.</span>
              <span className="shrink-0 font-semibold text-brand-700">Open Membership →</span>
            </button>
          </div>
        );
      case "comms":
        return (
          <div className="space-y-4">
            {/* COMMS-UX-01: the ONE truth block — channel states in words,
                reasons attached, edit switches behind one button. */}
            <ReachCard custno={customer.custno} />
            <PortalCommsCard custno={customer.custno} />
            {/* 360-AUDIT #17: automated sends (held/scheduled/sent) were
                invisible from the 360 — exactly where "did you email me?"
                gets asked. Same card the job workspace uses. */}
            <SentComms custno={customer.custno} />
            <CustomerMessages
              custno={customer.custno}
              locno={selectedLocation?.locno}
              multiProperty={customer.locations.length > 1}
            />
          </div>
        );
      case "history":
        return (
          <CustomerHistory
            custno={customer.custno}
            locno={selectedLocation?.locno}
            multiProperty={customer.locations.length > 1}
            customerName={displayCustomerName(null, customer.fullname)}
          />
        );
      case "notes":
        return (
          <CustomerNotes
            customer={customer}
            selectedLocation={selectedLocation}
          />
        );
      case "files":
        return (
          <CustomerDocuments
            custno={customer.custno}
            locno={selectedLocation?.locno}
            locations={customer.locations}
          />
        );
    }
  }
}
